import * as CookieThough from 'cookie-though';
import { t } from 'i18next';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useAnalyticsReporter } from './index';

export const HIDE_COOKIE_ON_PATHS = [
  '/',
  '/login',
  '/logout',
  '/legal/consent',
  '/legal/privacy-policy',
  '/legal/terms-of-use',
];

export function showCookieThough() {
  CookieThough.show();
}

export function useCookies() {
  const location = useLocation();
  const analyticsReporter = useAnalyticsReporter();
  const hasRunOnceRef = useRef(false);

  useEffect(() => {
    if (hasRunOnceRef.current) {
      return;
    }
    // This avoids flashing banner before redirects.
    if (HIDE_COOKIE_ON_PATHS.includes(location.pathname)) {
      return;
    }

    CookieThough.init({
      policies: [
        {
          id: 'mandatory',
          label: t('mandatoryCookies'),
          description: t('cookieEssentialDesc'),
          category: 'essential',
        },
        {
          id: 'analytical',
          label: t('analyticalCookies'),
          category: 'statistics',
          description: t('cookieStatisticsDesc'),
        },
      ],
      essentialLabel: t('alwaysOn'),
      permissionLabels: {
        accept: t('accept'),
        acceptAll: t('acceptAll'),
        decline: t('decline'),
      },
      cookiePreferenceKey: 'cookie-preferences',
      header: {
        title: t('cookieHeaderTitle'),
        subTitle: t('cookieHeaderSubLabel'),
        description: t('cookieHeaderDesc'),
      },
      cookiePolicy: {
        url: 'https://www.ppds.com/cookie-policy',
        label: t('cookiePolicyLabel'),
      },
      customizeLabel: t('customize'),
    });

    // This __must__ be attached after init or `onPreferencesChanged` inits cookie-though with defaults.
    CookieThough.onPreferencesChanged(() => {
      analyticsReporter.initialize();
      analyticsReporter.track('preferencesChanged');
    });

    // This __must__ be invoked after init because it calls `isAnalyticalCookieEnabled` which in turn would
    // init cookie-though with defaults by calling `getPreferences`.
    analyticsReporter.initialize();

    hasRunOnceRef.current = true;
  }, [analyticsReporter, location.pathname]);
}

export function isAnalyticalCookieEnabled(): boolean {
  const analyticalCookieOption = CookieThough.getPreferences().cookieOptions.find(
    (option) => option.id === 'analytical',
  );

  return analyticalCookieOption?.isEnabled ?? false;
}

export const cookieBannerStyle = {
  '.cookie-though': {
    '--ct-slider-enabled-primary': '#1D85E8',
    '--ct-slider-enabled-secondary': 'var(--chakra-colors-blue-100)',
    '--ct-primary-200': 'var(--chakra-colors-blue-100)',
    '--ct-primary-100': 'var(--chakra-colors-blue-50)',
    '--ct-primary-font': "'Gilroy'",
    '--ct-primary-font-weight': 600,
    '--ct-secondary-font': "'Inter'",
    '--ct-primary-400': 'var(--chakra-colors-blue-700)',
    '--ct-primary-300': 'var(--chakra-colors-blue-500)',
    '--ct-text': 'var(--chakra-colors-blue-800)',
  },
};
